import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { Network, AlertCircle, AlertTriangle, Plus, Trash2, ListPlus } from 'lucide-react';
import {
  BIT_RATE_PRESETS,
  DEFAULT_BIT_RATE_BPS,
  PERIOD_PRESETS_MS,
  FRAME_TYPES,
  DLC_OPTIONS,
  DEFAULT_ROWS,
  STATUS_STYLES,
  makeRow,
} from '../../lib/canBusLoadTools';
import { canBusLoadToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;

const CanBusLoadCalculator = () => {
  const [bitRatePreset, setBitRatePreset] = useState(String(DEFAULT_BIT_RATE_BPS));
  const [customBitRate, setCustomBitRate] = useState('');
  const [stuffing, setStuffing] = useState('off');
  const [rows, setRows] = useState(DEFAULT_ROWS);
  const [sortBy, setSortBy] = useState('none');
  const [quickCount, setQuickCount] = useState('5');
  const [quickPeriod, setQuickPeriod] = useState('10');

  const bitRateBps = bitRatePreset === 'custom' ? Number(customBitRate) : Number(bitRatePreset);

  const [resultsById, setResultsById] = useState({});
  const [summary, setSummary] = useState(null);
  const [duplicateCanIds, setDuplicateCanIds] = useState([]);
  const [busError, setBusError] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      canBusLoadToolApi.convert({
        bitRateBps,
        stuffing,
        rows: rows.map((r) => ({ id: r.id, canIdHex: r.canIdHex, frameType: r.frameType, dlc: r.dlc, periodMs: r.periodMs })),
      })
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          setRequestError(null);
          if (outcome.errors) {
            setBusError(outcome.errors.bitRate || 'Invalid bus settings.');
            setResultsById({});
            setSummary(null);
            setDuplicateCanIds([]);
          } else {
            setBusError(null);
            const byId = {};
            outcome.results.rows.forEach((r) => { byId[r.id] = r; });
            setResultsById(byId);
            setSummary(outcome.results.summary);
            setDuplicateCanIds(outcome.results.duplicateCanIds || []);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setRequestError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [rows, bitRateBps, stuffing]);

  const updateRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
  const addBlankRow = () => setRows((prev) => [...prev, makeRow()]);
  const clearAll = () => setRows([]);

  const addQuickRows = () => {
    const count = Math.max(1, Math.min(200, Math.round(Number(quickCount)) || 0));
    const periodMs = Number(quickPeriod);
    if (!count || !periodMs || periodMs <= 0) return;
    const newRows = Array.from({ length: count }, () => makeRow({ periodMs }));
    setRows((prev) => [...prev, ...newRows]);
  };

  const displayRows = useMemo(() => {
    if (sortBy === 'none') return rows;
    const copy = [...rows];
    if (sortBy === 'period') copy.sort((a, b) => a.periodMs - b.periodMs);
    if (sortBy === 'canId') copy.sort((a, b) => (parseInt(a.canIdHex, 16) || 0) - (parseInt(b.canIdHex, 16) || 0));
    return copy;
  }, [rows, sortBy]);

  const statusStyle = summary ? STATUS_STYLES[summary.status] : STATUS_STYLES.green;

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="technical-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
              <Network className="h-5 w-5 text-autoxium-blue" />
              CAN Bus Load Calculator
            </CardTitle>
            <CardDescription>
              Build a table of periodic CAN messages and see the resulting bus load %
              update live. All calculations run server-side.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Bit Rate
                </Label>
                <Select value={bitRatePreset} onValueChange={setBitRatePreset}>
                  <SelectTrigger data-testid="can-bus-bitrate-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BIT_RATE_PRESETS.map((b) => (
                      <SelectItem key={b.value} value={String(b.value)}>{b.label}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom…</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {bitRatePreset === 'custom' && (
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                    Custom Bit Rate (bps)
                  </Label>
                  <Input
                    type="number"
                    step="any"
                    value={customBitRate}
                    onChange={(e) => setCustomBitRate(e.target.value)}
                    className="font-mono"
                    placeholder="e.g. 800000"
                    data-testid="can-bus-custom-bitrate-input"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Bit Stuffing
                </Label>
                <Select value={stuffing} onValueChange={setStuffing}>
                  <SelectTrigger data-testid="can-bus-stuffing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off / Ideal</SelectItem>
                    <SelectItem value="worst">Worst-case stuffing (~+20%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Sort Table By
                </Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger data-testid="can-bus-sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Entry Order</SelectItem>
                    <SelectItem value="period">Period</SelectItem>
                    <SelectItem value="canId">CAN ID</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {stuffing === 'worst' && (
              <p className="text-xs text-muted-foreground">
                Worst-case stuffing is a simplified approximation (~1.2× the unstuffed
                frame bit count), not an exact bit-by-bit stuffing simulation.
              </p>
            )}
            {busError && (
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="can-bus-bitrate-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {busError}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={`technical-card ring-1 ${statusStyle.ring}`} data-testid="can-bus-summary">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Total Bus Load</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className={`font-mono text-5xl font-black ${statusStyle.text}`} data-testid="can-bus-total-load">
                {summary ? summary.totalLoadPercent.toFixed(2) : '0.00'}%
              </p>
              <Badge variant="outline" className={`font-mono text-[10px] mt-2 ${statusStyle.text}`}>
                {statusStyle.label}
              </Badge>
              {summary?.overCapacity && (
                <div className="flex items-center gap-1.5 text-xs text-destructive font-bold mt-2">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Exceeds 100% bus capacity.
                </div>
              )}
            </div>
            <div className="pt-3 border-t border-structure space-y-1.5 text-xs font-mono">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Messages / sec</span>
                <span className="font-bold">{summary ? summary.totalMessagesPerSecond.toFixed(2) : '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Bandwidth Used</span>
                <span className="font-bold">{summary ? summary.totalBandwidthBps.toFixed(0) : '0'} bps</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Headroom</span>
                <span className="font-bold">
                  {summary ? summary.headroomPercent.toFixed(2) : '100.00'}%
                  {' '}({summary ? Math.max(summary.headroomBps, 0).toFixed(0) : bitRateBps || 0} bps)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {duplicateCanIds.length > 0 && (
        <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-500/10 rounded-md px-4 py-3" data-testid="can-bus-duplicate-warning">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Duplicate CAN ID(s) in use: {duplicateCanIds.join(', ')}. Not blocked, but worth reviewing.</span>
        </div>
      )}
      {requestError && (
        <div className="flex items-center gap-2 text-sm text-destructive" data-testid="can-bus-request-error">
          <AlertCircle className="h-4 w-4 shrink-0" /> {requestError}
        </div>
      )}

      <Card className="technical-card">
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Messages</CardTitle>
            <CardDescription>Each row is one periodic CAN message.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="number"
              min={1}
              max={200}
              value={quickCount}
              onChange={(e) => setQuickCount(e.target.value)}
              className="h-8 w-16 font-mono text-xs"
              data-testid="can-bus-quick-count-input"
            />
            <span className="text-xs text-muted-foreground">@</span>
            <Select value={quickPeriod} onValueChange={setQuickPeriod}>
              <SelectTrigger className="h-8 w-24 text-xs" data-testid="can-bus-quick-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_PRESETS_MS.map((p) => (
                  <SelectItem key={p} value={String(p)}>{p} ms</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="sm" onClick={addQuickRows} data-testid="can-bus-quick-add-btn">
              <ListPlus className="h-3.5 w-3.5" /> Quick Add
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addBlankRow} data-testid="can-bus-add-row-btn">
              <Plus className="h-3.5 w-3.5" /> Add Row
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={clearAll} data-testid="can-bus-clear-all-btn">
              Clear All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {displayRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="can-bus-empty-state">
              No messages yet. Add a row or use Quick Add above — bus load shows 0% until rows are added.
            </p>
          ) : (
            <Table data-testid="can-bus-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>CAN ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>DLC</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Freq (Hz)</TableHead>
                  <TableHead className="text-right">Bits/Frame</TableHead>
                  <TableHead className="text-right">Bandwidth (bps)</TableHead>
                  <TableHead className="text-right">Load %</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayRows.map((row) => {
                  const r = resultsById[row.id];
                  const fieldErrors = r && !r.valid ? r.errors : {};
                  const isCustomPeriod = row.periodIsCustom || !PERIOD_PRESETS_MS.includes(row.periodMs);
                  return (
                    <TableRow key={row.id} data-testid={`can-bus-row-${row.id}`}>
                      <TableCell>
                        <Input
                          value={row.name}
                          onChange={(e) => updateRow(row.id, { name: e.target.value })}
                          placeholder="e.g. EngineSpeed"
                          className="h-8 text-xs w-32"
                          data-testid={`can-bus-name-${row.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.canIdHex}
                          onChange={(e) => updateRow(row.id, { canIdHex: e.target.value })}
                          placeholder="e.g. 100"
                          className={`h-8 text-xs w-24 font-mono ${fieldErrors.canId ? 'border-destructive' : ''}`}
                          title={fieldErrors.canId || ''}
                          aria-invalid={!!fieldErrors.canId}
                          data-testid={`can-bus-canid-${row.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select value={row.frameType} onValueChange={(v) => updateRow(row.id, { frameType: v })}>
                          <SelectTrigger className="h-8 w-32 text-xs" data-testid={`can-bus-frametype-${row.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FRAME_TYPES.map((f) => (
                              <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={String(row.dlc)} onValueChange={(v) => updateRow(row.id, { dlc: Number(v) })}>
                          <SelectTrigger
                            className={`h-8 w-16 text-xs ${fieldErrors.dlc ? 'border-destructive' : ''}`}
                            title={fieldErrors.dlc || ''}
                            data-testid={`can-bus-dlc-${row.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DLC_OPTIONS.map((d) => (
                              <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Select
                            value={isCustomPeriod ? 'custom' : String(row.periodMs)}
                            onValueChange={(v) => {
                              if (v === 'custom') updateRow(row.id, { periodIsCustom: true });
                              else updateRow(row.id, { periodMs: Number(v), periodIsCustom: false });
                            }}
                          >
                            <SelectTrigger className="h-8 w-24 text-xs" data-testid={`can-bus-period-select-${row.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PERIOD_PRESETS_MS.map((p) => (
                                <SelectItem key={p} value={String(p)}>{p} ms</SelectItem>
                              ))}
                              <SelectItem value="custom">Custom…</SelectItem>
                            </SelectContent>
                          </Select>
                          {isCustomPeriod && (
                            <Input
                              type="number"
                              step="any"
                              value={row.periodMs}
                              onChange={(e) => updateRow(row.id, { periodMs: Number(e.target.value) })}
                              className={`h-8 w-16 text-xs font-mono ${fieldErrors.period ? 'border-destructive' : ''}`}
                              title={fieldErrors.period || ''}
                              data-testid={`can-bus-period-custom-${row.id}`}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {r?.valid ? r.frequencyHz.toFixed(2) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {r?.valid ? r.bitsPerFrame.toFixed(1) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {r?.valid ? r.bandwidthBps.toFixed(0) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs font-bold text-autoxium-blue">
                        {r?.valid ? r.loadPercent.toFixed(3) : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => removeRow(row.id)}
                          aria-label="Remove row"
                          data-testid={`can-bus-remove-${row.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CanBusLoadCalculator;
